import React from "react";
import { Modal, Progress, Steps } from "antd";
import {
  UserOutlined,
  BankOutlined,
  ContainerOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { IdentifierProps } from "types";
import { useGetUserInfo } from "hooks/useGetUserInfo";

export const TalentProgress = ({
  open,
  handleClose,
  data,
}: IdentifierProps) => {
  const { userInfo } = useGetUserInfo();

  const isRecruiter = userInfo?.type === "recruiter";

  if (isRecruiter) {
    const progressData = data as any;

    const totalProgress = Number(progressData?.total ?? 0);

    const profileCompleted = Number(progressData?.profile_section ?? 0) === 100;

    const companyCompleted =
      Number(
        progressData?.company_section ??
          progressData?.company_details_section ??
          0,
      ) === 100;

    const jobCompleted =
      Number(
        progressData?.job_section ??
          progressData?.job_post_section ??
          progressData?.job_creation_section ??
          0,
      ) === 100;

    const steps = [
      {
        key: "profile",
        title: "Complete your profile",
        completed: profileCompleted,
        icon: <UserOutlined />,
        path: "/settings/profile",
      },
      {
        key: "company",
        title: "Update your Company Details",
        completed: companyCompleted,
        icon: <BankOutlined />,
        path: "/settings/profile-company",
      },
      {
        key: "job",
        title: "First Job Post Creation",
        completed: jobCompleted,
        icon: <ContainerOutlined />,
        path: "/jobs/create",
      },
    ];

    return (
      <Modal
        open={open}
        onCancel={() => handleClose(false)}
        footer={null}
        width={600}
        centered
        title={null}
        styles={{
          content: {
            padding: 0,
            borderRadius: 12,
            overflow: "hidden",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div className="px-9 pt-9 pb-8">
          <div className="pr-8">
            <h2 className="m-0 text-[18px] font-semibold text-[#111827]">
              Get Started
            </h2>

            <p className="mt-1 mb-0 text-[14px] text-[#718096]">
              Complete your registration to verify your account.
            </p>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1">
              <Progress
                percent={Math.min(Math.max(totalProgress, 0), 100)}
                showInfo={false}
                strokeColor="#42D600"
                trailColor="#F1F3F5"
                strokeWidth={7}
              />
            </div>

            <span className="min-w-[38px] text-right text-[14px] font-medium text-[#344054]">
              {Math.round(totalProgress)}%
            </span>
          </div>

          <div className="mt-5 flex flex-col gap-6">
            {steps.map((step) => (
              <Link
                key={step.key}
                to={step.path}
                onClick={() => handleClose(false)}
                className="group flex w-fit items-center gap-3 no-underline"
              >
                <div
                  className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border text-[17px] transition-all ${
                    step.completed
                      ? "border-[#071474] bg-[#071474] text-white"
                      : "border-[#071474] bg-white text-[#071474]"
                  }`}
                >
                  {step.completed ? (
                    <CheckOutlined className="text-[17px]" />
                  ) : (
                    step.icon
                  )}
                </div>

                <span className="text-[14px] font-medium text-[#071474] group-hover:underline">
                  {step.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Get Started"
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width="45rem"
      centered
    >
      <p>Complete your registration to verify your account.</p>

      <div className="pt-4">
        <div className="py-4">
          <Progress percent={data?.total} />
        </div>

        <Steps
          direction="vertical"
          current={data?.profile_section === 100 ? 1 : 0}
          className="text-primary"
          items={[
            {
              title: <Link to="/settings/profile">Complete your profile</Link>,
              description: "",
              status: data?.profile_section === 100 ? "finish" : "wait",
            },
          ]}
        />
      </div>
    </Modal>
  );
};
